import { formatCapitalize } from "./formatCapitalize.js";

export const CAMPAIGN_AUDIENCE_TYPE_LABELS = {
  all: "Everyone",
  schedule: "Scheduled trips",
  tripType: "Trip type",
  family: "Family",
  stop: "Stop",
  campus: "Campus",
};

const sortStrings = (values) =>
  [...values].filter(Boolean).map(String).sort((a, b) => a.localeCompare(b));

const sortedUniqueIds = (ids) => sortStrings(Array.isArray(ids) ? ids : []);

const getAudienceFilter = (record) => {
  if (!record || typeof record !== "object") return {};
  return record.audienceFilter && typeof record.audienceFilter === "object"
    ? record.audienceFilter
    : {};
};

export const normalizeCampaignScheduleDetails = (scheduleDetails) => {
  if (!Array.isArray(scheduleDetails)) return [];
  return scheduleDetails
    .map((schedule) => ({
      id: schedule?.scheduleId ?? schedule?.id ?? null,
      name: schedule?.scheduleName ?? schedule?.name ?? null,
      startTime: schedule?.scheduleStartTime ?? schedule?.startTime ?? null,
    }))
    .sort((a, b) =>
      String(a.id ?? a.name ?? "").localeCompare(String(b.id ?? b.name ?? "")),
    );
};

const getFamilyMemberName = (member) =>
  [member?.firstName, member?.lastName].filter(Boolean).join(" ").trim();

const getFamilyChildren = (family) => family?.children || family?.child || [];

const getAudienceStudentName = (child) => {
  if (!child || typeof child !== "object") return "";
  const preferred = child?.preferredName?.trim();
  const fullName =
    child?.childName ||
    child?.name ||
    [child?.firstName, child?.lastName].filter(Boolean).join(" ").trim();
  if (!fullName && preferred) return preferred;
  return fullName || "";
};

const getStudentNamesFromChildren = (children) =>
  (Array.isArray(children) ? children : [])
    .map(getAudienceStudentName)
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b));

export const normalizeCampaignFamilyDetails = (familyDetails) => {
  if (!Array.isArray(familyDetails)) return [];
  return familyDetails
    .map((family) => ({
      id: family?.familyId ?? family?.id ?? family?._id ?? null,
      title:
        (family?.parent || [])
          .map(getFamilyMemberName)
          .filter(Boolean)
          .join(", ") ||
        family?.parentName ||
        family?.name ||
        "Family",
      subtitle: (() => {
        const childNames = getFamilyChildren(family)
          .map(getFamilyMemberName)
          .filter(Boolean);
        const parts = [];
        if (childNames.length) {
          parts.push(
            childNames.length === 1
              ? `Child: ${childNames[0]}`
              : `Children: ${childNames.join(", ")}`,
          );
        }
        const address = family?.address || family?.parent?.[0]?.address;
        if (address) parts.push(address);
        return parts.join(" · ");
      })(),
      students: getStudentNamesFromChildren(getFamilyChildren(family)),
    }))
    .sort((a, b) =>
      String(a.id ?? a.title ?? "").localeCompare(String(b.id ?? b.title ?? "")),
    );
};

export const normalizeCampaignStopDetails = (stopDetails) => {
  if (!Array.isArray(stopDetails)) return [];
  return stopDetails
    .map((stop) => {
      const studentCount =
        typeof stop?.totalChildCount === "number"
          ? stop.totalChildCount
          : Array.isArray(stop?.associatedChilds)
            ? stop.associatedChilds.length
            : 0;
      const subtitleParts = [];
      if (studentCount > 0) {
        subtitleParts.push(
          `${studentCount} associated student${studentCount === 1 ? "" : "s"}`,
        );
      }
      if (stop?.address?.trim()) subtitleParts.push(stop.address.trim());

      return {
        id: stop?.stopId ?? stop?.id ?? stop?._id ?? null,
        title: stop?.name?.trim() || "Unnamed stop",
        subtitle: subtitleParts.join(" · "),
        students: getStudentNamesFromChildren(stop?.associatedChilds || stop?.children),
      };
    })
    .sort((a, b) =>
      String(a.id ?? a.title ?? "").localeCompare(String(b.id ?? b.title ?? "")),
    );
};

export const formatCampaignCampusLabel = (campus) => {
  const normalized = String(campus || "")
    .trim()
    .toLowerCase();
  if (normalized === "primary") return "Primary";
  if (normalized === "secondary") return "Secondary";
  return formatCapitalize(normalized.replace(/_/g, " "));
};

export const normalizeCampaignCampusDetails = (campusDetails, campuses = []) => {
  const fromDetails = Array.isArray(campusDetails)
    ? campusDetails
        .map((entry) => entry?.campus ?? entry?.value ?? entry)
        .filter(Boolean)
    : [];

  const seen = new Set();
  const unique = [];
  for (const campus of [...fromDetails, ...campuses]) {
    const key = String(campus).trim().toLowerCase();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    unique.push(campus);
  }

  unique.sort((a, b) =>
    String(a).trim().toLowerCase().localeCompare(String(b).trim().toLowerCase()),
  );

  return unique.map((campus) => ({
    id: String(campus).trim().toLowerCase(),
    title: formatCampaignCampusLabel(campus),
    subtitle: null,
  }));
};

const normalizeCampaignTripTypeAudience = (record) => {
  const filter = getAudienceFilter(record);
  const tripTypeIds = sortedUniqueIds(
    filter.tripTypeIds ?? record?.tripTypeIds ?? [],
  );
  const tripTypeScope = filter.tripTypeScope ?? record?.tripTypeScope ?? "all";
  const tripTypeDate = filter.tripTypeDate ?? record?.tripTypeDate ?? "";
  return { tripTypeIds, tripTypeScope, tripTypeDate };
};

export const inferCampaignTargetAudience = (record) => {
  if (!record) return "all";

  const explicit = record.targetAudience;
  if (explicit && explicit !== "all") return explicit;

  const filter = getAudienceFilter(record);
  if (filter.tripTypeIds?.length || record?.tripTypeIds?.length) return "tripType";
  if (filter.familyIds?.length || record?.familyDetails?.length || record?.familyIds?.length) {
    return "family";
  }
  if (filter.stopIds?.length || record?.stopDetails?.length || record?.stopIds?.length) {
    return "stop";
  }
  if (filter.campuses?.length || record?.campusDetails?.length || record?.campus) {
    return "campus";
  }
  if (
    record?.scheduleDetails?.length ||
    record?.scheduleIds?.length ||
    record?.scheduleId
  ) {
    return "schedule";
  }

  return explicit || "all";
};

export const getCampaignAudienceTypeLabel = (record) => {
  const audience = inferCampaignTargetAudience(record);
  return CAMPAIGN_AUDIENCE_TYPE_LABELS[audience] || formatCapitalize(audience);
};

export const hasDisplayableCampaignAudience = (record) => {
  if (!record) return false;

  const audience = inferCampaignTargetAudience(record);
  if (audience === "all") return true;

  const filter = getAudienceFilter(record);

  switch (audience) {
    case "schedule":
      return (
        normalizeCampaignScheduleDetails(record.scheduleDetails).length > 0 ||
        sortedUniqueIds(record.scheduleIds).length > 0 ||
        Boolean(record.scheduleId)
      );
    case "tripType":
      return normalizeCampaignTripTypeAudience(record).tripTypeIds.length > 0;
    case "family":
      return (
        normalizeCampaignFamilyDetails(record.familyDetails).length > 0 ||
        sortedUniqueIds(filter.familyIds ?? record.familyIds).length > 0
      );
    case "stop":
      return (
        normalizeCampaignStopDetails(record.stopDetails).length > 0 ||
        sortedUniqueIds(filter.stopIds ?? record.stopIds).length > 0
      );
    case "campus":
      return (
        normalizeCampaignCampusDetails(
          record.campusDetails,
          filter.campuses ?? (record.campus ? [record.campus] : []),
        ).length > 0
      );
    default:
      return Boolean(record.targetAudience);
  }
};

export const mergeCampaignAudienceRecord = (record, sibling) => {
  const mergedFilter = {
    ...getAudienceFilter(sibling),
    ...getAudienceFilter(record),
  };

  const scheduleDetails =
    record?.scheduleDetails ?? sibling?.scheduleDetails ?? [];
  const scheduleIds = record?.scheduleIds ?? sibling?.scheduleIds ?? [];
  const familyDetails = record?.familyDetails ?? sibling?.familyDetails ?? [];
  const stopDetails = record?.stopDetails ?? sibling?.stopDetails ?? [];
  const campusDetails = record?.campusDetails ?? sibling?.campusDetails ?? [];

  const targetAudience =
    inferCampaignTargetAudience(record) !== "all"
      ? inferCampaignTargetAudience(record)
      : inferCampaignTargetAudience(sibling);

  return {
    ...(sibling || {}),
    ...(record || {}),
    targetAudience,
    audienceFilter: mergedFilter,
    scheduleDetails,
    scheduleIds,
    familyDetails,
    stopDetails,
    campusDetails,
    tripTypeIds: mergedFilter.tripTypeIds ?? record?.tripTypeIds ?? sibling?.tripTypeIds ?? [],
    tripTypeScope:
      mergedFilter.tripTypeScope ?? record?.tripTypeScope ?? sibling?.tripTypeScope ?? "all",
    tripTypeDate:
      mergedFilter.tripTypeDate ?? record?.tripTypeDate ?? sibling?.tripTypeDate ?? "",
    familyIds: mergedFilter.familyIds ?? record?.familyIds ?? sibling?.familyIds ?? [],
    stopIds: mergedFilter.stopIds ?? record?.stopIds ?? sibling?.stopIds ?? [],
    campuses: mergedFilter.campuses ?? record?.campuses ?? sibling?.campuses ?? [],
    campus: record?.campus ?? sibling?.campus ?? "",
  };
};

export const serializeCampaignAudience = (record) => {
  if (!record) return "";

  const audience = inferCampaignTargetAudience(record);
  const filter = getAudienceFilter(record);

  switch (audience) {
    case "all":
      return "all";
    case "schedule": {
      const scheduleDetails = normalizeCampaignScheduleDetails(record.scheduleDetails);
      if (scheduleDetails.length > 0) {
        return `schedule:${JSON.stringify(scheduleDetails)}`;
      }
      const scheduleIds = sortedUniqueIds(
        record.scheduleIds?.length ? record.scheduleIds : [record.scheduleId],
      );
      return `schedule:${JSON.stringify(scheduleIds)}`;
    }
    case "tripType":
      return `tripType:${JSON.stringify(normalizeCampaignTripTypeAudience(record))}`;
    case "family": {
      const familyDetails = normalizeCampaignFamilyDetails(record.familyDetails);
      if (familyDetails.length > 0) {
        return `family:${JSON.stringify(familyDetails)}`;
      }
      return `family:${JSON.stringify(sortedUniqueIds(filter.familyIds ?? record.familyIds))}`;
    }
    case "stop": {
      const stopDetails = normalizeCampaignStopDetails(record.stopDetails);
      if (stopDetails.length > 0) {
        return `stop:${JSON.stringify(stopDetails)}`;
      }
      return `stop:${JSON.stringify(sortedUniqueIds(filter.stopIds ?? record.stopIds))}`;
    }
    case "campus":
      return `campus:${JSON.stringify(
        normalizeCampaignCampusDetails(
          record.campusDetails,
          filter.campuses ?? (record.campus ? [record.campus] : []),
        ),
      )}`;
    default:
      return audience;
  }
};

export const getCampaignAudienceDetailItems = (record) => {
  if (!record) return [];

  const audience = inferCampaignTargetAudience(record);
  const filter = getAudienceFilter(record);

  switch (audience) {
    case "all":
      return [];
    case "schedule": {
      const scheduleDetails = normalizeCampaignScheduleDetails(record.scheduleDetails);
      if (scheduleDetails.length > 0) {
        return scheduleDetails.map((schedule) => ({
          title: schedule.name || schedule.id || "Unnamed trip",
          subtitle: schedule.startTime || null,
        }));
      }
      const scheduleIds = sortedUniqueIds(
        record.scheduleIds?.length ? record.scheduleIds : [record.scheduleId],
      );
      return scheduleIds.map((id) => ({ title: id, subtitle: null }));
    }
    case "tripType": {
      const { tripTypeIds, tripTypeScope, tripTypeDate } =
        normalizeCampaignTripTypeAudience(record);
      const scopeLabel =
        tripTypeScope === "date" && tripTypeDate?.trim()
          ? `Trips on ${tripTypeDate.trim()}`
          : "All upcoming trips";
      if (tripTypeIds.length === 0) {
        return [{ title: "Trip types", subtitle: scopeLabel }];
      }
      return tripTypeIds.map((id) => ({
        title: typeof id === "object" ? id.name || id.label || id.id : String(id),
        subtitle: scopeLabel,
      }));
    }
    case "family": {
      const familyDetails = normalizeCampaignFamilyDetails(record.familyDetails);
      if (familyDetails.length > 0) {
        return familyDetails.map(({ title, subtitle, students }) => ({
          title,
          subtitle,
          students,
        }));
      }
      return sortedUniqueIds(filter.familyIds ?? record.familyIds).map((id) => ({
        title: id,
        subtitle: null,
      }));
    }
    case "stop": {
      const stopDetails = normalizeCampaignStopDetails(record.stopDetails);
      if (stopDetails.length > 0) {
        return stopDetails.map(({ title, subtitle, students }) => ({
          title,
          subtitle,
          students,
        }));
      }
      return sortedUniqueIds(filter.stopIds ?? record.stopIds).map((id) => ({
        title: id,
        subtitle: null,
      }));
    }
    case "campus":
      return normalizeCampaignCampusDetails(
        record.campusDetails,
        filter.campuses ?? (record.campus ? [record.campus] : []),
      ).map(({ title }) => ({ title, subtitle: null }));
    default:
      return [{ title: formatCapitalize(audience), subtitle: null }];
  }
};
